import { messageForErrorCode } from '@repo/api-client';
import type { SyncBlockedEntry, SyncStatus } from '@repo/desktop-bridge';
import { PARK_TIME_ZONE } from '@repo/desktop-bridge';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconAlertTriangle, IconArchiveOff, IconDeviceDesktopOff } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';

import { messageForFailure } from '@/api/failure';
import { desktopSync, desktopSyncRequeue } from '@/desktop/bridge';

import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { describeQueuedWrite } from './queued-write-description';

/**
 * The writes this terminal took and could not deliver.
 *
 * **It reads the device, not the API.** A till acknowledges a sale the moment it
 * is durably queued, which is what keeps the gate issuing tickets through an
 * outage, and the queue is a SQLite file on that device. So this screen has no
 * API call in it: it asks the Electron shell it is running inside. Opened in a
 * browser there is nothing to ask, and it says so rather than showing an empty
 * table — a back-office machine has no queue, and cannot see another device's.
 *
 * What it exists for is the pile at the end. Most refusals retry until they
 * land; a few cannot, and every one of those is a write the server judged and
 * would judge the same way if it were offered again *now* — a device revoked
 * since the sale, a cashier taken off this outlet, a day already closed. Those
 * stop, and stopping is only safe if somebody finds out. Retrying them
 * automatically would put a permanent failure at the head of a queue that has
 * to stay in order, where it would hold up everything behind it until the end of
 * time.
 *
 * **But stopping is not the same as being stuck for good, and until FE047 it
 * was.** `blocked` had no exit at all, and no session-authenticated route
 * creates a terminal-originated record (blossom-rest-api#301) — so a refused
 * sale could not be sent and could not be keyed in either, which is a receipted
 * sale lost outright. Each row now carries a control that offers it to the queue
 * again, for the case the refusal describes something somebody has since put
 * right. It is a person's judgement rather than a timer, which is exactly the
 * distinction that made an automatic retry wrong here.
 *
 * The count in the header is the same number seen from the other side, so
 * somebody who never opens this screen still learns that it has something in it.
 */

type LoadState =
  | { readonly kind: 'no-terminal' }
  | { readonly kind: 'loading' }
  | {
      readonly kind: 'loaded';
      readonly counts: SyncStatus;
      readonly rows: readonly SyncBlockedEntry[];
    }
  | { readonly kind: 'failed'; readonly error: unknown };

/** Kept next to the header cells below, so the two cannot drift. */
const COLUMNS = 6;

/**
 * The park's own clock and the park's own order — 7 Aug 2026, 09:42.
 *
 * In Munnar's zone, never the machine's: this screen is only drawn on a till,
 * and the tills ship on US Pacific (#331).
 */
/**
 * One refused write, named across every queue on the terminal.
 *
 * Each registration keeps its own queue (#243), so a row id is unique only
 * within one of them — a Gate row 7 and a Glass Bridge row 7 are two sales.
 */
const rowKey = (write: SyncBlockedEntry): string =>
  `${String(write.registration)}:${String(write.id)}`;

const WHEN = new Intl.DateTimeFormat('en-IN', {
  timeZone: PARK_TIME_ZONE,
  dateStyle: 'medium',
  timeStyle: 'short',
});

export function QueuedWrites() {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  // A counter rather than a boolean, so two refreshes in a row are two distinct
  // values and the second one still fires.
  const [reloads, setReloads] = useState(0);

  // Which row is being offered back, so its own control can say so without
  // disabling the others. A row rather than a boolean: five refusals from one
  // outage are five independent decisions, and somebody working down the list
  // should not be locked out of row four because row three is in flight.
  const [sending, setSending] = useState<string | null>(null);

  // A failed *ask*, which is not a failed load — the list is still on screen
  // and still correct. Kept apart from `state` for that reason: replacing the
  // table with an error would take away the very rows the person is working
  // through.
  const [sendError, setSendError] = useState<unknown>(null);

  // Refresh is somebody asking for the current state, so a failed ask from
  // earlier stops being news. Cleared here rather than in the effect below,
  // which also runs on the reload a send triggers and would wipe the message
  // the send had just set.
  const reload = useCallback(() => {
    setSendError(null);
    setReloads((count) => count + 1);
  }, []);

  /**
   * Offer one refused write to the queue again.
   *
   * The id and nothing else crosses the bridge. The stored bytes go back under
   * the original key, so the server answers it as a replay — recording a write
   * whose earlier refusal released the key, or recognising one it already holds
   * and answering from the stored outcome. Rebuilding any part of the request
   * here would break both, so nothing here builds one.
   *
   * Reloaded rather than patched in place. The terminal is the only thing that
   * knows what state the row is in now, and a screen that removed the row
   * itself would be guessing on behalf of a queue that may already have drained
   * it, refused it again, or never taken it.
   */
  const sendAgain = useCallback((write: SyncBlockedEntry) => {
    const sync = desktopSyncRequeue();

    if (sync === null) {
      return;
    }

    setSending(rowKey(write));
    setSendError(null);

    sync
      .requeue(write.id, write.registration)
      .catch((error: unknown) => {
        setSendError(error);
      })
      .finally(() => {
        setSending(null);
        setReloads((count) => count + 1);
      });
  }, []);

  // Whether this shell can be asked at all. A bundle served over `app://` can be
  // newer than the Electron process around it, and drawing a control that calls
  // a method the shell does not have is a `TypeError` in front of somebody
  // looking for a missing sale.
  const canSendAgain = desktopSyncRequeue() !== null;

  useEffect(() => {
    const sync = desktopSync();

    if (sync === null) {
      setState({ kind: 'no-terminal' });

      return;
    }

    // No AbortController: these are IPC calls to the process this window belongs
    // to, and there is nothing to cancel. `live` still matters — a resolution
    // landing after this effect was torn down would set state on a screen that
    // has gone.
    let live = true;

    setState({ kind: 'loading' });

    Promise.all([sync.status(), sync.blocked()])
      .then(([counts, rows]) => {
        if (live) setState({ kind: 'loaded', counts, rows });
      })
      .catch((error: unknown) => {
        if (live) setState({ kind: 'failed', error });
      });

    return () => {
      live = false;
    };
  }, [reloads]);

  return (
    <Screen
      title="Queued writes"
      description="Sales and other actions taken at this terminal that have not reached the server, and the ones it refused for good."
      aside={
        state.kind === 'no-terminal' ? undefined : (
          <>
            {state.kind === 'loaded' ? (
              <>
                <ScreenFact label="Waiting to send">
                  <span className="font-mono numeric">
                    {state.counts.pending + state.counts.inFlight}
                  </span>
                </ScreenFact>
                <ScreenFact label="Refused">
                  <span className="font-mono numeric">{state.counts.blocked}</span>
                </ScreenFact>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Reading this terminal’s queue…</p>
            )}

            <Button variant="outline" onClick={reload} disabled={state.kind === 'loading'}>
              Refresh
            </Button>
          </>
        )
      }
    >
      {state.kind === 'no-terminal' ? (
        <NotATerminal />
      ) : (
        <>
          {state.kind === 'loaded' && state.rows.length > 0 && (
            <TheseCannotBeEnteredByHand canSendAgain={canSendAgain} />
          )}

          {sendError !== null && <CouldNotAsk error={sendError} />}

          <ScreenTable label="Writes this terminal could not deliver">
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Who</TableHead>
                {/* No width hint, deliberately. This column now carries the
                    longest prose on the screen and the one beside it is capped
                    at `max-w-prose`, so auto layout gives the room to the
                    sentence about what was lost — which is the fact the row is
                    for. Pinning it at `w-80` wrapped it to four lines beside a
                    half-empty refusal column. */}
                <TableHead>What</TableHead>
                <TableHead>Why it stopped</TableHead>
                <TableHead className="text-right">Attempts</TableHead>
                {/* Named for a screen reader and not for the eye, the way
                    `Terminals` names its own action column: the button in it
                    already says what it does and to which write, and a visible
                    heading would be a sixth label saying less. */}
                <TableHead className="w-44 text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>

            {state.kind === 'loading' && <TableSkeleton columns={COLUMNS} />}

            {state.kind === 'failed' && (
              <TableNotice columns={COLUMNS}>
                <LoadFailed error={state.error} onRetry={reload} />
              </TableNotice>
            )}

            {state.kind === 'loaded' &&
              (state.rows.length === 0 ? (
                <TableNotice columns={COLUMNS}>
                  <EmptyState
                    title="Nothing is waiting on anybody"
                    description="Writes appear here only when the server refuses one in a way retrying cannot fix — a revoked terminal, or a staff member no longer assigned to this outlet. An empty list is the ordinary state."
                  />
                </TableNotice>
              ) : (
                <TableBody>
                  {state.rows.map((row) => (
                    <RefusedRow
                      key={rowKey(row)}
                      write={row}
                      canSendAgain={canSendAgain}
                      sending={sending === rowKey(row)}
                      onSendAgain={() => {
                        sendAgain(row);
                      }}
                    />
                  ))}
                </TableBody>
              ))}
          </ScreenTable>
        </>
      )}
    </Screen>
  );
}

function RefusedRow({
  write,
  canSendAgain,
  sending,
  onSendAgain,
}: {
  write: SyncBlockedEntry;
  canSendAgain: boolean;
  sending: boolean;
  onSendAgain: () => void;
}) {
  return (
    <TableRow>
      <TableCell className="align-top whitespace-nowrap numeric">
        {WHEN.format(new Date(write.occurredAt))}
      </TableCell>

      <TableCell className="align-top font-medium text-foreground numeric">
        {/* Only possible for a write queued before this device recorded actors.
            Saying so beats a blank cell, which reads as a rendering fault. */}
        {write.staffCode ?? <span className="text-muted-foreground">Not recorded</span>}

        {/* Where it was sold, on a terminal that sells for more than one outlet
            (#243) — the second question about a refused sale, since each outlet
            closes its own day. Absent where the terminal was never told a name,
            which is every till that has only ever held one registration. */}
        {write.outletName !== null && (
          <p className="mt-1 text-sm font-normal text-muted-foreground">{write.outletName}</p>
        )}
      </TableCell>

      <TableCell className="align-top">
        <WhatItWas write={write} />
      </TableCell>

      <TableCell className="max-w-prose align-top">
        {/* The code becomes words in one place for the whole app. A screen that
            worded this itself would re-separate refusals the API merges on
            purpose — and this is the screen most tempted to, because it is the
            one looking at the codes.

            The copy there addresses whoever hit the refusal, and here somebody
            is reading about a refusal that happened to a colleague, so it can
            land as second-person prose about a third party. That is a trade
            worth making: one wording per code across the app is the rule, and
            the code itself is printed underneath for the reader who needs the
            fact rather than the sentence. */}
        <p>{messageForErrorCode(write.lastError ?? undefined)}</p>

        {/* Which fields the office says disagree, when the refusal named any —
            `SALES_SALE_IMMUTABLE` is the one that does, and it means that sale
            id is recorded with facts other than the ones this till sent.

            **The names, and only the names.** The server sends which fields
            differ and never what it holds in them, so there is no diff to show:
            a column of stored values would be this screen inventing the half of
            the comparison it has never seen, about a sale whose money is already
            in the drawer. Whoever settles this reads the office's copy from the
            office. */}
        {write.differingFields.length > 0 && (
          <p className="mt-1 text-sm">
            The office’s copy of this sale differs in{' '}
            <span className="numeric">{write.differingFields.join(', ')}</span>. It does not say
            what it holds in them — compare against the record in the back office.
          </p>
        )}

        {/* What gets read down the phone: the code, and the correlation id that
            ties this row to the API's own account of the refusal. On screen
            rather than in a tooltip, because a tooltip cannot be read aloud from
            a printout or a photograph of the till. */}
        <p className="mt-1 text-xs text-muted-foreground numeric">
          {write.lastError ?? 'No code came back'}
          {write.lastRequestId !== null && <> · Reference {write.lastRequestId}</>}
        </p>
      </TableCell>

      <TableCell className="text-right align-top numeric">{write.attempts}</TableCell>

      <TableCell className="text-right align-top">
        {canSendAgain && (
          <Button variant="outline" onClick={onSendAgain} disabled={sending}>
            {sending ? 'Sending…' : 'Send again'}
            {/* The visible label first and the write it belongs to after it, so
                the accessible name still opens with the words on the button —
                and so a table of these is not a column of controls a screen
                reader announces identically. `Terminals` puts the prefix in
                every one of its action names for the same reason. */}
            <span className="sr-only">
              {' '}
              — {describeQueuedWrite(write.scope)?.what ?? write.scope} from{' '}
              {WHEN.format(new Date(write.occurredAt))}
            </span>
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

/**
 * What this write was, and what it cost that it never arrived.
 *
 * **The one thing the row could not say before.** Every other cell is about the
 * delivery — when it was tried, by whom, how many times, what the server
 * answered. None of that reaches the question somebody actually opens this
 * screen holding: a visitor is inside the garden, or a drawer is heavy, and
 * nothing on the server knows. The refusal beside this explains why it stopped;
 * this explains what stopped.
 *
 * Derived from the write's scope and never from its error code, which is the
 * whole reason this is a screen change and not a line of copy. The refusal that
 * strands a shape of queued write (blossom-rest-api#301) arrives as
 * `VALIDATION_FAILED` — raised app-wide, mostly a software fault, and left on
 * the generic fallback by 115 for good reasons that still hold. The scope is
 * what distinguishes a stranded admission from a stranded refund, and it is
 * already on the entry.
 */
function WhatItWas({ write }: { write: SyncBlockedEntry }) {
  const description = describeQueuedWrite(write.scope);

  // A scope this build has never been taught — a row written by a newer one, or
  // by a ticket this screen has not caught up with. The queue is a file on the
  // device and outlives any single release, so this is a state to render rather
  // than one to rule out. The scope itself is the only true thing there is to
  // say, so it is said in the voice a machine string is given everywhere in this
  // app, and nothing is invented around it.
  if (description === null) {
    return <p className="font-mono text-sm text-foreground">{write.scope}</p>;
  }

  return (
    <>
      <p className="font-medium text-foreground">{description.what}</p>

      <p className="mt-1 text-sm text-muted-foreground">{description.stake}</p>

      {/* The pair case, said as far as it can honestly be said.
          
          A till can hold a queued ticket and a queued void of that same ticket.
          Strand the first and the second can never apply — and because the
          drain steps over a quarantined entry rather than stopping (`sync/
          engine.ts`, deliberately: "a queue that cannot quarantine is a till
          that stops selling"), both land in this list, side by side, looking
          like two independent failures.

          They are not, and this row cannot prove which pair it belongs to:
          `SyncBlockedEntry` carries the scope and not the payload, so a void
          cannot name the ticket it voids. The choice made here is to say the
          conditional thing that is true of every void and every refund rather
          than to widen the bridge for a field this screen would be the only
          caller of. "If" is doing real work in that sentence — the parent may
          have gone through perfectly well an hour before this one was made. */}
      {description.actsOnAnExistingRecord && (
        <p className="mt-1 text-sm text-muted-foreground">
          It acts on a record the server must already hold. If the write that recorded it is also in
          this list, that one has to be sent first.
        </p>
      )}

      {/* The scope itself, kept for the same reason the error code below is kept:
          it is what gets read down the phone to whoever can look at the API's
          own logs. Mono because it is a value typed back in rather than prose —
          `docs/design-language.md`, "Type". */}
      <p className="mt-1 font-mono text-xs text-muted-foreground">{write.scope}</p>
    </>
  );
}

/**
 * The ask itself failed — the shell did not answer, or answered by throwing.
 *
 * Above the table rather than in place of it. The rows are still on screen and
 * still correct: what failed is one request to the process this window belongs
 * to, and taking the list away would remove the very thing the person is
 * working through. `destructive`, unlike the standing notice below, because
 * this is something refusing right now rather than a fact about the system.
 */
function CouldNotAsk({ error }: { error: unknown }) {
  return (
    <Alert variant="destructive">
      <IconAlertTriangle />
      <AlertTitle>This terminal could not be asked to send it again</AlertTitle>
      <AlertDescription>
        {messageForFailure(error)} Nothing has changed — the write is still here, and it can be
        tried again.
      </AlertDescription>
    </Alert>
  );
}

/**
 * The one thing this screen owes anybody who is looking at a row on it — and
 * the half of it that survived FE047.
 *
 * **Every entry in this queue is a write that originated at a terminal**, and
 * the API has no session-authenticated route that creates any of those records.
 * Issuing a ticket, voiding one, recording a sale and refunding one are all
 * terminal-only; the back office's ticket and sale routes are reads, a void and
 * a refund, and every one of them operates on a record that must already exist.
 * So a refused terminal write still cannot be re-entered anywhere — not by a
 * manager, not by an owner, not from the office. That is the standing limit, it
 * is filed as blossom-rest-api#301, and this half of the sentence goes the day
 * a route exists to make it false.
 *
 * **What changed is that these are no longer stuck for good.** Until FE047 the
 * queue had no way out of `blocked` at all, so the two facts together meant a
 * receipted sale could be lost outright: nothing would send it and nothing could
 * record it. Each row now carries a control that offers the same write back to
 * the queue, and this notice has to say so — copy promising a manager that a
 * quarantined write can never be recorded is worse than no copy at all once a
 * button beside it does exactly that.
 *
 * It says *when* pressing is worth anything, because the honest answer is "not
 * always". The server refused these for a reason and most of those reasons are
 * still true; what a person knows and the queue cannot is whether the reason has
 * since been dealt with.
 *
 * **Not `destructive`, and not an empty-queue reassurance either.** Red is for
 * something the system is refusing right now; this is a standing limit, and the
 * rows underneath already carry their own refusals. It appears only when there
 * is something to say it about — a permanent notice over an empty table would
 * train everybody to stop reading it.
 */
function TheseCannotBeEnteredByHand({ canSendAgain }: { canSendAgain: boolean }) {
  return (
    <Alert>
      <IconArchiveOff />
      <AlertTitle>These can be sent again, but they cannot be entered by hand</AlertTitle>
      <AlertDescription>
        <p>
          Everything below was taken at this terminal, and the server refused it. There is no screen
          anywhere — here or in the back office — that can record one of these instead: tickets and
          sales are only ever created by a terminal. The money was taken and the paper is in
          somebody’s hand, so the record stays here where it can be seen.
        </p>
        {canSendAgain ? (
          <p>
            Once the reason a write stopped has been dealt with — the terminal registered again, the
            staff member put back on this outlet — <strong>Send again</strong> puts it back in the
            queue exactly as it was. That is safe to press: the same write goes back under the same
            reference, so the server either records it or recognises one it already has, never both.
            A refusal that still stands simply happens again and the row comes back. Report anything
            that will not clear, with its number.
          </p>
        ) : (
          <p>
            This terminal is running a version of the app that cannot offer these back to the queue.
            Update it and they can be sent again; a terminal that has just been updated also offers
            them once by itself when it starts. Until then, report each one with its number.
          </p>
        )}
      </AlertDescription>
    </Alert>
  );
}

/**
 * Opened somewhere that has no queue — which is every browser.
 *
 * Not an error and not an empty state. Both of those would imply this machine
 * has a queue and it happens to be clear, and somebody would then read a quiet
 * back office as evidence that no till is stuck.
 */
function NotATerminal() {
  return (
    <Alert>
      <IconDeviceDesktopOff />
      <AlertTitle>This queue lives on the terminal</AlertTitle>
      <AlertDescription>
        Each till holds its own queue on the device, so there is nothing to show in a browser. Open
        this screen on the terminal you want to look at.
      </AlertDescription>
    </Alert>
  );
}
